import { useState } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {Eye, EyeOff, KeyRound, Shield} from 'lucide-react'

interface TrocaSenhaObrigatoriaProps {
  consultoraId: string
  nome: string
  onConcluido: () => void
}

function senhaValida(senha: string): string | null {
  if (senha.length < 8) return 'A nova senha deve ter pelo menos 8 caracteres'
  if (!/\d/.test(senha)) return 'A nova senha deve conter pelo menos 1 número'
  return null
}

export default function TrocaSenhaObrigatoria({ consultoraId, nome, onConcluido }: TrocaSenhaObrigatoriaProps) {
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [mostrar, setMostrar] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const primeiro = (nome || '').trim().split(/\s+/)[0] || 'consultora'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const erro = senhaValida(novaSenha)
    if (erro) {
      toast.error(erro)
      return
    }
    if (novaSenha !== confirmarSenha) {
      toast.error('As senhas não coincidem')
      return
    }

    setSalvando(true)
    try {
      await lumi.entities.consultoras.update(consultoraId, {
        senha: novaSenha.trim(),
        precisaTrocarSenha: false,
        primeiroAcesso: false,
        updatedAt: new Date().toISOString(),
      })
      toast.success('Senha atualizada! Acesso liberado.')
      onConcluido()
    } catch (err) {
      console.error('[troca-senha-obrigatoria] erro', err)
      toast.error('Erro ao salvar a nova senha. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#D8F3DC] rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-[#2D6A4F]" />
          </div>
          <h1 className="text-3xl font-cormorant font-semibold text-[#2C2C2A]">
            Troque sua senha
          </h1>
          <p className="text-sm text-[#5F5E5A] mt-2 leading-relaxed">
            Olá, <strong>{primeiro}</strong>! Por segurança, crie uma senha pessoal antes de acessar o painel.
          </p>
        </div>

        <div className="bg-[#FFFBEB] border border-[#F59E0B] rounded-xl px-4 py-3 mb-6 text-sm text-[#92400E] flex items-start gap-2">
          <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Esta etapa é obrigatória no primeiro acesso com a senha provisória.</span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-[#5F5E5A] mb-2 font-medium">Nova senha</label>
            <div className="relative">
              <input
                type={mostrar ? 'text' : 'password'}
                value={novaSenha}
                onChange={e => setNovaSenha(e.target.value)}
                className="w-full px-4 py-3 pr-11 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
                placeholder="Mínimo 8 caracteres e 1 número"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setMostrar(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B08968]"
                aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {mostrar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-[#5F5E5A] mb-2 font-medium">Confirmar nova senha</label>
            <input
              type={mostrar ? 'text' : 'password'}
              value={confirmarSenha}
              onChange={e => setConfirmarSenha(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
              placeholder="Digite a senha novamente"
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            disabled={salvando}
            className="w-full bg-[#2D6A4F] text-white py-3 rounded-full font-medium hover:bg-opacity-90 transition-all disabled:opacity-60"
          >
            {salvando ? 'Salvando...' : 'Salvar e acessar o painel'}
          </button>
        </form>
      </div>
    </div>
  )
}
